import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, Btn, TextArea, SectionTitle, EmptyState } from "@/lib/ui";
import { HistorySidebar } from "@/components/HistorySidebar";
import { Users2, Cpu, AlertTriangle, TrendingUp, ListChecks, Gavel } from "lucide-react";

const EXAMPLES = [
  "Should we launch a new service?",
  "Review Monarch performance.",
  "Prepare next month's group strategy.",
];
const TONE_TEXT = { err: "text-err", ok: "text-ok", info: "text-ggg", gold: "text-primary" };
const LS_KEY = "ggg:meeting:active";

function SynthBlock({ icon: Icon, title, items, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border border-border rounded-md p-3">
      <div className={`flex items-center gap-2 mb-2 ${TONE_TEXT[tone]}`}>
        <Icon className="h-4 w-4" />
        <SectionTitle className={TONE_TEXT[tone]}>{title}</SectionTitle>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-foreground/90 flex gap-2"><span className="text-muted-foreground">›</span>{it}</li>
        ))}
      </ul>
    </div>
  );
}

function Round({ round, index, total }) {
  const s = round.synthesis;
  return (
    <div className="border border-border/60 rounded-md p-3">
      {total > 1 && (
        <div className="text-[10px] font-mono uppercase tracking-wider text-primary mb-2">
          Round {index + 1} · {round.topic}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-3">
          <SectionTitle>Executive Perspectives</SectionTitle>
          {(round.perspectives || []).map((p) => (
            <Panel key={p.code} className="p-4 border-l-2" style={{ borderLeftColor: "hsl(var(--ggg))" }}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-head font-bold">{p.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase">{p.code} · {p.model}</div>
                </div>
                <StatusBadge tone="ok" dot>Contributed</StatusBadge>
              </div>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{p.perspective}</p>
            </Panel>
          ))}
        </div>
        <div className="lg:col-span-5 space-y-3">
          <SectionTitle>Executive Synthesis</SectionTitle>
          <Panel className="p-4"><p className="text-sm leading-relaxed whitespace-pre-wrap">{s?.synthesis}</p></Panel>
          <SynthBlock icon={AlertTriangle} title="Risks" items={s?.risks} tone="err" />
          <SynthBlock icon={TrendingUp} title="Opportunities" items={s?.opportunities} tone="ok" />
          <SynthBlock icon={ListChecks} title="Recommended Actions" items={s?.recommended_actions} tone="info" />
          <SynthBlock icon={Gavel} title="Decisions Requiring Owner Approval" items={s?.decisions_requiring_approval} tone="gold" />
        </div>
      </div>
    </div>
  );
}

export default function MeetingRoom() {
  const api = useApi();
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [activeId, setActiveId] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [showArchived, setShowArchived] = useState(false);
  const bootstrapped = useRef(false);

  const meetings = useQuery({ queryKey: ["meetings", showArchived], queryFn: () => api.get(`/meetings${showArchived ? "?include_archived=true" : ""}`) });
  const meeting = useQuery({
    queryKey: ["meeting", activeId],
    queryFn: () => api.get(`/meetings/${activeId}`),
    enabled: !!activeId,
  });

  useEffect(() => {
    if (bootstrapped.current || !meetings.data) return;
    bootstrapped.current = true;
    if (!activeId && meetings.data.length > 0) setActiveId(meetings.data[0].id);
  }, [meetings.data, activeId]);

  useEffect(() => {
    if (activeId) localStorage.setItem(LS_KEY, activeId); else localStorage.removeItem(LS_KEY);
  }, [activeId]);

  const convene = useMutation({
    mutationFn: () => api.post("/meetings", { topic }),
    onSuccess: (r) => { setActiveId(r.id); setTopic(""); qc.invalidateQueries({ queryKey: ["meetings"] }); qc.setQueryData(["meeting", r.id], r); toast.success("Executive meeting complete"); },
    onError: (e) => toast.error(e.message),
  });
  const resume = useMutation({
    mutationFn: () => api.post(`/meetings/${activeId}/continue`, { topic }),
    onSuccess: (r) => { setTopic(""); qc.setQueryData(["meeting", r.id], r); qc.invalidateQueries({ queryKey: ["meetings"] }); toast.success("Discussion continued"); },
    onError: (e) => toast.error(e.message),
  });

  const newMeeting = () => { setActiveId(null); setTopic(""); };
  const rename = (id, title) => api.patch(`/meetings/${id}`, { title }).then(() => qc.invalidateQueries({ queryKey: ["meetings"] }));
  const archive = (id, archived = true) => api.patch(`/meetings/${id}`, { archived }).then(() => { toast.success(archived ? "Archived" : "Restored"); if (archived && id === activeId) newMeeting(); qc.invalidateQueries({ queryKey: ["meetings"] }); });
  const del = (id) => api.del(`/meetings/${id}`).then(() => { toast.success("Deleted"); if (id === activeId) newMeeting(); qc.invalidateQueries({ queryKey: ["meetings"] }); });

  const busy = convene.isPending || resume.isPending;
  const m = meeting.data;
  const rounds = m?.rounds || [];

  return (
    <div>
      <PageHeader kicker="GGG-02" title="Executive Meeting Room">
        <StatusBadge tone="proc" dot="pulse">MeshAPI Live</StatusBadge>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4">
        <div className="hidden lg:block" style={{ maxHeight: "calc(100vh - 12rem)" }}>
          <HistorySidebar
            items={meetings.data || []} activeId={activeId} loading={meetings.isLoading}
            onSelect={setActiveId} onNew={newMeeting} onRename={rename} onArchive={archive} onDelete={del}
            newLabel="New Meeting" heading="Meetings" testidPrefix="meeting"
            showArchived={showArchived} onToggleArchived={setShowArchived}
          />
        </div>

        <div>
          <Panel className="p-4 mb-5">
            <TextArea rows={2}
              label={activeId ? "Continue this meeting with a follow-up" : "Proposal or question for the executive team"}
              placeholder="e.g. Should we launch a new service under Monarch Estates?"
              value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="meeting-topic" />
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {!activeId && EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setTopic(ex)}
                  className="text-xs border border-border rounded-full px-3 py-1 text-muted-foreground hover:text-foreground transition-colors">{ex}</button>
              ))}
              <div className="flex-1" />
              {activeId ? (
                <Btn onClick={() => resume.mutate()} disabled={busy || !topic.trim()} data-testid="meeting-continue">
                  {resume.isPending ? <Cpu className="h-4 w-4 animate-spin" /> : <Users2 className="h-4 w-4" />} Continue Discussion
                </Btn>
              ) : (
                <Btn onClick={() => convene.mutate()} disabled={busy || !topic.trim()} data-testid="meeting-convene">
                  {convene.isPending ? <Cpu className="h-4 w-4 animate-spin" /> : <Users2 className="h-4 w-4" />} Convene Executives
                </Btn>
              )}
            </div>
          </Panel>

          {busy && (
            <div className="text-sm text-muted-foreground flex items-center gap-2 py-8">
              <Cpu className="h-4 w-4 animate-spin text-proc" /> Executives are deliberating via MeshAPI…
            </div>
          )}

          {!activeId && !busy && (
            <EmptyState icon={Users2} title="No meeting convened yet"
              hint="Enter a proposal above. Relevant AI executives each give their perspective, then the Executive Meeting AI produces a synthesis, risks, opportunities and decisions. Past meetings appear in the sidebar and can be resumed anytime." />
          )}

          {activeId && m && !busy && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <SectionTitle>{m.title}</SectionTitle>
                <StatusBadge tone="muted">{rounds.length} round{rounds.length === 1 ? "" : "s"}</StatusBadge>
              </div>
              {rounds.map((r, i) => <Round key={i} round={r} index={i} total={rounds.length} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
